import { Link } from "react-router-dom";
import { Music, StickyNote } from "lucide-react";
import { computeMonthlyProgress } from "../lib/progress";
import { formatTime12h } from "../lib/schedule";
import { getStudentTypeStyle } from "../lib/studentTypes";
import { useSettings } from "../context/SettingsContext";

const INITIAL_COLORS = [
    "bg-action-soft text-action",
    "bg-brand-soft text-brand",
    "bg-success-soft text-success",
    "bg-warning-soft text-warning",
    "bg-mist text-ink",
    "bg-utility/40 text-brand",
];

function getInitials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}

function hashIndex(str, mod) {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return h % mod;
}

export default function StudentCard({ student, attendance }) {
    const { settings } = useSettings();
    const { units, target, pct, complete, cycle } = computeMonthlyProgress(
        student,
        attendance,
        undefined,
        settings.monthlyTargetMultiplier,
    );
    const initialsColor = INITIAL_COLORS[hashIndex(student.id, INITIAL_COLORS.length)];
    const typeStyle = getStudentTypeStyle(student.type);
    const TypeIcon = typeStyle.icon;
    const scheduleSummary = student.days.length
        ? student.days
              .map((d) => `${d.slice(0, 3)} ${formatTime12h(student.schedule?.[d]) || ""}`.trim())
              .join(" · ")
        : "No schedule";

    return (
        <Link
            to={`/students/${student.id}`}
            data-testid={`student-card-${student.id}`}
            className="group block bg-white rounded-3xl border border-mist card-soft p-6 transition-all duration-300 hover:-translate-y-1"
        >
            <div className="flex items-start gap-4 mb-5">
                <div
                    className={`w-14 h-14 rounded-2xl ${initialsColor} font-display font-bold text-xl flex items-center justify-center shrink-0`}
                >
                    {getInitials(student.name)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-xl text-slate-900 truncate flex items-center gap-2">
                        <span className="truncate">{student.name}</span>
                        {student.notes && student.notes.trim() && (
                            <span
                                className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-warning-soft text-warning"
                                title="Has notes"
                                data-testid={`student-card-notes-indicator-${student.id}`}
                            >
                                <StickyNote size={11} strokeWidth={3} />
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-0.5">
                        <Music size={14} strokeWidth={2.5} />
                        <span className="font-semibold">{student.instrument}</span>
                    </div>
                </div>
                <span
                    className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${typeStyle.chip}`}
                >
                    <TypeIcon size={11} strokeWidth={3} />
                    {student.type}
                </span>
            </div>

            <div className="text-xs font-semibold text-slate-500 mb-4 line-clamp-2">
                {scheduleSummary}
            </div>

            <div className="flex items-end justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    Month {cycle}
                </span>
                <span className="font-display font-bold text-sm text-slate-800">
                    {units} / {target || 0}
                </span>
            </div>
            <div className="h-2.5 rounded-full bg-mist overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all ${
                        complete ? "bg-success" : "bg-[#3D6385]"
                    }`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            {complete && (
                <div className="mt-3 text-xs font-bold text-success uppercase tracking-wider">
                    ✓ Target reached
                </div>
            )}
        </Link>
    );
}
